import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Users, Mail, Trash2, X } from "lucide-react";
import UserProfileLink from "@/components/UserProfileLink";

export default function CollaboratorsPanel({ ideaId, currentUser, ownerUserId, assignedMentorId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: "", role: "collaborator", note: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/ideas/${ideaId}/collaborators`);
      setItems(r.data);
    } catch (e) { /* 403 if not accessible */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ideaId]);

  const isOwner = currentUser?.user_id === ownerUserId;
  const isMentor = currentUser?.linked_mentor_id && assignedMentorId === currentUser.linked_mentor_id;
  const canInvite = isOwner || isMentor || currentUser?.role === "admin";

  const invite = async (e) => {
    e?.preventDefault();
    if (!form.email) return;
    setSubmitting(true);
    try {
      await api.post(`/ideas/${ideaId}/invite`, form);
      toast.success("Invite recorded");
      setForm({ email: "", role: "collaborator", note: "" });
      setShowInvite(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const remove = async (cid) => {
    if (!window.confirm("Remove this collaborator?")) return;
    try {
      await api.delete(`/ideas/${ideaId}/collaborators/${cid}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const active = items.filter(i => i.status === "active" || i.status === "pending_signup");

  return (
    <div className="kb-card p-6" data-testid="collaborators-panel">
      <div className="flex items-center justify-between">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted inline-flex items-center gap-2">
          <Users className="w-3.5 h-3.5" /> COLLABORATORS ({active.length})
        </div>
        {canInvite && !showInvite && (
          <button onClick={() => setShowInvite(true)} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1" data-testid="invite-collab-btn">
            <UserPlus className="w-3 h-3" /> Invite
          </button>
        )}
      </div>
      <p className="text-xs text-kb-ink-muted mt-2">Co-researchers can view the workspace, milestones, todos & chat — same access as the project owner.</p>

      {showInvite && (
        <form onSubmit={invite} className="mt-3 space-y-2 border-t border-slate-200 pt-3" data-testid="invite-form">
          <input className="kb-input text-sm" placeholder="Email of collaborator (must be a KhojByte user)"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
            data-testid="invite-email-input" />
          <div className="grid grid-cols-2 gap-2">
            <select className="kb-input text-sm" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="collaborator">Collaborator</option>
              <option value="reviewer">Reviewer</option>
            </select>
            <input className="kb-input text-sm" placeholder="Note (optional)"
              value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={submitting} className="kb-btn-primary text-xs flex-1 justify-center" data-testid="invite-submit-btn">
              {submitting ? "Sending…" : "Send invite"}
            </button>
            <button type="button" onClick={() => setShowInvite(false)} className="kb-btn-outline text-xs"><X className="w-3 h-3" /></button>
          </div>
        </form>
      )}

      <div className="mt-4 space-y-2">
        {loading ? <div className="text-xs text-kb-ink-muted">Loading…</div> :
          active.length === 0 ? <div className="text-xs text-kb-ink-muted">No collaborators yet.</div> :
          active.map((c) => (
            <div key={c.id} className="flex items-center gap-2 border border-slate-200 rounded-sm p-2 text-xs" data-testid={`collab-${c.id}`}>
              <Mail className="w-3.5 h-3.5 text-kb-ink-light shrink-0" />
              <div className="flex-1 min-w-0">
                {c.user_id ? (
                  <UserProfileLink userId={c.user_id} name={c.user_name || c.invited_email} className="font-medium text-xs" />
                ) : (
                  <span className="text-kb-ink-muted">{c.invited_email}</span>
                )}
                <div className="kb-mono text-[10px] text-kb-ink-light">{c.role_in_project} · {c.status === "pending_signup" ? "Pending signup" : "Active"}</div>
              </div>
              {canInvite && (
                <button onClick={() => remove(c.id)} className="text-rose-600 p-1" data-testid={`collab-${c.id}-remove`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
